import { FC, useEffect, useState } from 'react';
import Layout from 'antd/lib/layout/layout';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { Auth0Provider, withAuthenticationRequired, useAuth0 } from '@auth0/auth0-react';
import { AppMenu } from './components/menu/AppMenu';
import AppHeader from './components/menu/AppHeader';
import AppContent from './components/menu/AppContent';
import PdfContainer from './routes/pdf/pdfContainer';
import TrialPage from './routes/trial';
import { fetchAuthorizedAPIEndpoint } from './utils';
import { FeatureFlag } from './types/userType';

export const useMediaQuery = (query: string) => {
  const mediaMatch = window.matchMedia(query);
  const [matches, setMatches] = useState(mediaMatch.matches);

  useEffect(() => {
    const handler = (e: any) => {
      setMatches(e.matches);
    };
    mediaMatch.addEventListener('change', handler);
    return () => mediaMatch.removeEventListener('change', handler);
  }, []);
  return matches;
};

const App: FC = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isRowBased = useMediaQuery('(max-width: 768px)');
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const [licenseEndTs, setLicenseEndTs] = useState<number | null | undefined>(undefined);

  const history = useHistory();

  useEffect(() => {
    getAccessTokenSilently().then((token) => {
      setAccessToken(token);
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!accessToken) return;

    fetchAuthorizedAPIEndpoint('/user/get_trial_period', accessToken)
      .then((resp) => {
        if (resp.ok) return resp.json();
        else throw resp.json();
      })
      .then((data: { LicenseEndTs: number }) => {
        setLicenseEndTs(data.LicenseEndTs);
      });
  }, [accessToken]);

  const userIsSubscribed = () => {
    if (licenseEndTs === null || licenseEndTs === undefined) return false;
    return licenseEndTs - Date.now() > 0;
  };

  if (!user) return null;
  if (licenseEndTs === undefined) return null;
  if (!user.feature_flags.includes(FeatureFlag.RoleAryballe) && !userIsSubscribed()) history.push('/home');

  return (
    <>
      {isRowBased ? (
        <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'row' }} hasSider>
          <AppMenu isCollapsed={true} setIsCollapsed={setIsCollapsed} />
          <Layout style={{ marginLeft: 0 }}>
            <AppHeader isCollapsed={true} />
            <AppContent />
          </Layout>
        </Layout>
      ) : (
        <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'row' }} hasSider>
          <AppMenu isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
          <Layout style={{ marginLeft: isCollapsed ? 60 : 240 }}>
            <AppHeader isCollapsed={isCollapsed} />
            <AppContent />
          </Layout>
        </Layout>
      )}
    </>
  );
};

const OKTAApp: FC = () => {
  return (
    <Auth0Provider
      domain="https://login.aryballe.com"
      clientId="OyKNlIKe1KM25RVKS4Bf9iFS3NMZVEUl"
      authorizationParams={{
        redirect_uri: window.location.origin + '/signin/callback',
        scope: 'openid email profile assigned_groups multisource',
        audience: 'https://tools.aryballe.com/api/',
      }}
    >
      <Switch>
        <Route path="/signout/callback">
          <Redirect to="/" />
        </Route>
        <Route path="/:sessionID/pdf" component={PdfContainer} />
        <Route path="/home" component={TrialPage} />
        <Route path="/" component={withAuthenticationRequired(App)} />
      </Switch>
    </Auth0Provider>
  );
};

export default OKTAApp;
